<template>
    <b-container fluid class="project-group-management py-3">
        <b-row>
            <b-col cols="12">
                <div class="d-flex justify-content-between align-items-end border-bottom mb-3 pb-3">
                    <div>
                        <h3 class="mb-1">평가 그룹 관리</h3>
                        <p class="fw-400 text-secondary mb-0">
                            프로젝트에 연결하는 평가 그룹(project group)을 생성/수정/삭제합니다.
                            그룹 코드는 프론트 빌드와 약속된 값이라 생성 후에는 바꿀 수 없습니다.
                        </p>
                    </div>
                </div>
            </b-col>
        </b-row>

        <b-row>
            <b-col cols="12">
                <b-card no-body class="shadow-sm" header-bg-variant="white">
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between flex-wrap">
                            <div class="d-flex align-items-center">
                                <h5 class="mb-0">평가 그룹 목록</h5>
                                <b-badge v-if="!loading" pill variant="primary" class="ml-2">
                                    {{ groups.length }}건
                                </b-badge>
                            </div>
                            <b-btn size="sm" variant="primary" :disabled="loading" @click="openCreate">
                                <fa icon="plus" class="mr-2" />평가 그룹 추가
                            </b-btn>
                        </div>
                    </template>

                    <b-table
                        class="mb-0 sticky-header-sort-fix"
                        :items="groups"
                        :fields="fields"
                        :busy="loading"
                        show-empty
                        hover
                        responsive="sm"
                        sticky-header="600px"
                        no-border-collapse
                        empty-html="<h6 class='mt-3 gray-400'>등록된 평가 그룹이 없습니다.</h6>"
                    >
                        <template #table-busy>
                            <div class="d-flex align-items-center justify-content-center my-4">
                                <b-spinner class="mr-2" variant="primary" />
                                <span>불러오는 중...</span>
                            </div>
                        </template>

                        <template #cell(group_code)="{ value }">
                            <code>{{ value }}</code>
                        </template>

                        <template #cell(report_type)="{ item }">
                            <b-badge v-if="item.report_type === 'EV_COUNT'" variant="info">
                                EV_COUNT (v3)
                            </b-badge>
                            <span v-else class="text-secondary">기본 계산식 (v2)</span>
                        </template>

                        <template #cell(is_active)="{ value }">
                            <b-badge pill :variant="value ? 'success' : 'secondary'">
                                {{ value ? '활성' : '비활성' }}
                            </b-badge>
                        </template>

                        <template #cell(memo)="{ value }">
                            <span v-if="value" style="word-break: break-all;">{{ value }}</span>
                            <span v-else class="text-muted">-</span>
                        </template>

                        <template #cell(actions)="{ item }">
                            <b-btn size="sm" variant="outline-secondary" class="mr-1" @click="openEdit(item)">
                                수정
                            </b-btn>
                            <b-btn size="sm" variant="outline-danger" :disabled="deletingId === item.id" @click="confirmDelete(item)">
                                <b-spinner v-if="deletingId === item.id" small />
                                <span v-else>삭제</span>
                            </b-btn>
                        </template>
                    </b-table>
                </b-card>
            </b-col>
        </b-row>

        <!-- 생성/수정 공용 모달 -->
        <ProjectGroupFormModal
            :modal-id="formModalId"
            :mode="formMode"
            :group="editingGroup"
            @saved="onSaved"
        />
    </b-container>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import ProjectGroupFormModal from './ProjectGroupFormModal.vue';

export default {
    name: 'ProjectGroupManagement',
    components: { ProjectGroupFormModal },
    data() {
        return {
            groups: [],
            loading: false,
            deletingId: null,
            formModalId: 'project-group-form-modal',
            formMode: 'create',
            editingGroup: {},
            fields: [
                { key: 'group_code', label: '그룹 코드', thStyle: 'width: 22%;' },
                { key: 'group_name', label: '그룹명', thStyle: 'width: 24%;' },
                { key: 'report_type', label: '보고서 엔진', thStyle: 'width: 14%;' },
                { key: 'is_active', label: '활성', thStyle: 'width: 8%;' },
                { key: 'memo', label: '메모' },
                { key: 'actions', label: '관리', thClass: 'text-center', tdClass: 'text-nowrap', thStyle: 'width: 12%;' },
            ],
        };
    },
    created() {
        this.fetchGroups();
    },
    methods: {
        async fetchGroups() {
            this.loading = true;
            try {
                // 관리 화면은 비활성 그룹도 봐야 하므로 include_inactive=true 로 전부 조회한다.
                const { data } = await this.$axios.get('/project_code/groups', {
                    params: { include_inactive: true },
                });
                this.groups = Array.isArray(data) ? data : [];
            } catch (e) {
                this.$bvToast.toast('평가 그룹 목록을 불러오지 못했습니다.', { title: '오류', variant: 'danger' });
            } finally {
                this.loading = false;
            }
        },
        openCreate() {
            this.formMode = 'create';
            this.editingGroup = {};
            this.$bvModal.show(this.formModalId);
        },
        openEdit(item) {
            this.formMode = 'edit';
            this.editingGroup = { ...item };
            this.$bvModal.show(this.formModalId);
        },
        onSaved() {
            // 생성/수정 후 목록을 다시 불러와 정렬(group_code 오름차순)을 서버 기준으로 유지한다.
            this.fetchGroups();
        },
        async confirmDelete(item) {
            const ok = await this.$bvModal.msgBoxConfirm(
                `'${item.group_name}' (${item.group_code}) 그룹을 삭제하시겠습니까?`,
                {
                    title: '평가 그룹 삭제',
                    okVariant: 'danger',
                    okTitle: '삭제',
                    cancelTitle: '취소',
                    centered: true,
                },
            );
            if (!ok) return;

            this.deletingId = item.id;
            try {
                await this.$axios.delete(`/project_code/groups/${item.id}`);
                this.$bvToast.toast(`'${item.group_name}' 그룹을 삭제했습니다.`, { title: '삭제 완료', variant: 'success' });
                await this.fetchGroups();
            } catch (e) {
                this.handleDeleteError(e);
            } finally {
                this.deletingId = null;
            }
        },
        handleDeleteError(e) {
            const status = e && e.response && e.response.status;
            const serverMsg = this.extractError(e);
            if (status === 409) {
                // 참조중: 삭제 대신 비활성화를 안내한다.
                this.$bvModal.msgBoxOk(
                    serverMsg
                        || '이 그룹을 쓰는 프로젝트가 있어 삭제할 수 없습니다. 대신 수정 화면에서 비활성(활성 여부 off)으로 숨겨 주세요.',
                    { title: '삭제할 수 없음', okVariant: 'warning', okTitle: '확인', centered: true },
                );
                return;
            }
            let msg;
            if (status === 403) {
                msg = serverMsg || '권한이 없습니다. 관리자 계정으로 로그인했는지 확인해 주세요.';
            } else if (status === 404) {
                msg = serverMsg || '이미 삭제된 그룹입니다. 목록을 새로고침합니다.';
                this.fetchGroups();
            } else {
                msg = serverMsg || '삭제 중 오류가 발생했습니다.';
            }
            this.$bvToast.toast(msg, { title: '오류', variant: 'danger', noAutoHide: true });
        },
        extractError(e) {
            const data = e && e.response && e.response.data;
            return getErrorMessage(e, '') || (data && (data.msg || data.detail)) || '';
        },
    },
};
</script>

<style lang="scss" scoped>
.project-group-management {
    code {
        color: #d63384;
        word-break: break-all;
    }
}
</style>
